import type { LucideIcon } from 'lucide-react'
import { cn } from 'lib/utils'

export type ViewToggleOption<T extends string> = {
	value: T
	label: string
	icon: LucideIcon
}

export type ViewToggleProps<T extends string> = {
	value: T
	onChange: (value: T) => void
	options: ViewToggleOption<T>[]
	className?: string
}

/**
 * Pill-group view-mode selector. Renders `options` as a row of icon + text
 * buttons with a single active state. Generic over the string-literal union
 * of option values so callers keep exhaustive type-checking on `value` and
 * `onChange`.
 */
export function ViewToggle<T extends string>({
	value,
	onChange,
	options,
	className,
}: ViewToggleProps<T>) {
	return (
		<div className={cn('inline-flex items-center rounded-lg bg-gray-100 p-0.5', className)}>
			{options.map(({ value: v, label, icon: Icon }) => {
				const active = value === v
				return (
					<button
						key={v}
						type="button"
						onClick={() => onChange(v)}
						className={cn(
							'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all',
							active
								? 'bg-white text-[#0080fe] shadow-sm'
								: 'text-gray-500 hover:text-gray-700',
						)}
					>
						<Icon className="h-3.5 w-3.5" />
						{label}
					</button>
				)
			})}
		</div>
	)
}
